/* eslint-disable */
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { motion } from "framer-motion";
import Layout from "@/components/Layout";
import PrivacyRightsReviewStep from "@/components/onboarding/PrivacyRightsReviewStep";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumericInput } from "@/components/ui/NumericInput";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  JurisdictionSelector,
  NexusRegistrationSection,
  JURIS,
} from "@/components/onboarding/JurisdictionSelector";
import { AuditRunOverlay, useAuditJobPolling } from "@/components/AuditRunOverlay";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Loader2, ArrowRight, ShieldCheck, Clock, Eye } from "lucide-react";

const REVENUE_TIERS = ["Under $100k", "$100k-$500k", "$500k-$2M", "$2M+", "$25M+"];
const TRANSACTION_TIERS = ["Under 50", "50-100", "100-200", "200+"];

// Piece 1 (2026-08-16) — States whose privacy-applicability gate is only
// reachable in Verified mode. Previously 11; reduced to 8 with the shared
// "Business signals" section on the Profile step: IL biometric + WA health
// + NE SBA are now single-boolean questions gathered in BOTH modes, so their
// applicability gate resolves in Estimated mode too. The remaining 8 are
// gated on PER-STATE consumer counts — genuinely per-state numeric input
// only Verified mode can supply.
//
// CA is kept in the list because although its revenue-only path (≥$25M) is
// reachable via tier upper bound, its consumer-count path (100k+ CA
// consumers) is not — a sub-$25M merchant serving ≥100k CA consumers
// would silently miss applicability without Verified mode.
const ESTIMATED_MODE_LIMITED_STATES = [
  "CA", "VA", "CO", "IA", "IN", "KY", "MN", "TN",
];

// Piece 1B (2026-08-16) — Option B constants. Same 8 states, aliased for
// readability at the picker-render site. Kept as a separate name so future
// intent stays obvious ("this list is where the picker renders" vs
// "this list is where the auto-flip fired").
const COMPREHENSIVE_STATES = ESTIMATED_MODE_LIMITED_STATES;

// The 3 tier buckets. Values match the Pydantic contract in
// backend/models.py::OnboardingRequest.per_state_consumer_tier.
const CONSUMER_TIERS = [
  { value: "under_25k",   label: "Under 25k" },
  { value: "25k_to_100k", label: "25k–100k" },
  { value: "100k_plus",   label: "100k+" },
];

export default function Onboarding() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0=store, 1=profile, 2=policies, 3=confirmation
  const [busy, setBusy] = useState(false);

  // Store ownership
  const [storeUrl, setStoreUrl] = useState("");
  const [storeConfirmed, setStoreConfirmed] = useState(false);
  const [storeVerified, setStoreVerified] = useState(false);

  // Profile — shared
  const [scoreMode, setScoreMode] = useState("estimated"); // "estimated" | "verified"
  const [revenueTier, setRevenueTier] = useState("");
  // Session 2a — optional precise revenue in USD, shown when the merchant
  // picks the $2M+ or $25M+ tier. Empty string = merchant didn't provide.
  const [revenueExact, setRevenueExact] = useState("");
  const [transactionTier, setTransactionTier] = useState(""); // Quick Estimate
  const [selected, setSelected] = useState([]);
  const [focus, setFocus] = useState("");
  // Verified-mode exact numbers per state: { FL: { revenue: 250000, transactions: 80 } }
  const [perStateData, setPerStateData] = useState({});

  // Policies
  const [pp, setPp] = useState("");
  const [tos, setTos] = useState("");
  const [refund, setRefund] = useState("");
  const [cookieC, setCookieC] = useState(false);
  const [pcontact, setPcontact] = useState(false);
  const [retention, setRetention] = useState(false);
  const [jIdent, setJIdent] = useState(false);
  const [nDoc, setNDoc] = useState(false);
  const [aMon, setAMon] = useState(false);
  // Stage 3 (Prompt B) — 3 new signals for CA/VA/CO comprehensive scoring.
  // Null = unanswered (Provisional). false = No. true = Yes.
  // Sensitive-data: null unanswered · "yes" · "no" · "na" (Doesn't apply).
  const [hasCorrection, setHasCorrection] = useState(null);
  const [hasPortability, setHasPortability] = useState(null);
  const [hasSensitiveDisclosure, setHasSensitiveDisclosure] = useState(null);
  // Prompt B Batch 2 (2026-07-20) — Nebraska NDPA applicability toggle.
  // null = unanswered (Provisional). true = SBA small business (exempt).
  // false = NOT SBA small business (NDPA applies). Shown only when NE is
  // in the merchant's selected jurisdictions.
  const [isSbaSmallBusiness, setIsSbaSmallBusiness] = useState(null);
  // Prompt B Batch 4 (2026-07-22) — sector-specific privacy law gates + signals.
  // Applicability + 3 compliance signals for IL BIPA, 5 for WA MHMDA. All
  // Optional[bool] — null = unanswered (Provisional / not-yet-scored),
  // true = yes, false = no. Shown only when IL or WA is in selected states.
  const [collectsBiometric, setCollectsBiometric] = useState(null);
  const [hasBiometricConsent, setHasBiometricConsent] = useState(null);
  const [hasBiometricRetention, setHasBiometricRetention] = useState(null);
  const [hasBiometricNoSale, setHasBiometricNoSale] = useState(null);
  const [collectsHealth, setCollectsHealth] = useState(null);
  const [hasMhmdaPolicy, setHasMhmdaPolicy] = useState(null);
  const [hasHealthOptIn, setHasHealthOptIn] = useState(null);
  const [hasHealthSaleAuth, setHasHealthSaleAuth] = useState(null);
  const [hasHealthWithdrawal, setHasHealthWithdrawal] = useState(null);
  const [hasHealthDeletion, setHasHealthDeletion] = useState(null);
  // Piece 1B (2026-08-16) — Option B: per-state consumer-tier picker.
  // Dict mapping state abbr → "under_25k" | "25k_to_100k" | "100k_plus".
  // Rendered in Estimated mode only, only when a comprehensive-privacy
  // state (CA/VA/CO/IA/IN/KY/MN/TN) is in `selected`. Blank stays
  // Provisional per the approved plan — no guessed number ever.
  const [perStateConsumerTier, setPerStateConsumerTier] = useState({});
  // Horizontal A (2026-08-16) — Data broker self-attestation. Feeds the
  // ≥25k-consumer sale-of-PI applicability branch in
  // backend/services/scoring.py::_privacy_applicable for CA/VA/CO/IA/IN/KY/MN/TN.
  // null = unanswered (treated as False for scoring), true = data broker,
  // false = explicitly not a data broker. Shown only when any of the 8
  // comprehensive states is selected, alongside the consumer-tier picker.
  const [sellsPersonalData, setSellsPersonalData] = useState(null);
  const [scrapeStatus, setScrapeStatus] = useState({ privacy: null, terms: null, refund: null });
  const [firewallMsg, setFirewallMsg] = useState(null);
  // Per-state nexus registration: dynamic dict keyed by abbreviation
  const [regStates, setRegStates] = useState({});
  // Region filter for the 50-state selector
  const [jurisFilter, setJurisFilter] = useState("");

  // Mount-only profile prefill.
  useEffect(() => {
    api.get("/audit/me/profile").then(({ data }) => {
      const p = data?.profile;
      if (p) {
        setStoreUrl(p.shopify_store_url || "");
        setStoreVerified(!!p.verified_store_owner);
        if (p.verified_store_owner) setStep(1);
        if (p.score_mode === "verified" || p.score_mode === "estimated") setScoreMode(p.score_mode);        if (p.annual_revenue_tier) setRevenueTier(p.annual_revenue_tier);
        if (p.annual_revenue_exact !== undefined && p.annual_revenue_exact !== null) {
          setRevenueExact(String(p.annual_revenue_exact));
        }
        if (p.annual_transaction_tier) setTransactionTier(p.annual_transaction_tier);
        if (p.selected_jurisdictions?.length) setSelected(p.selected_jurisdictions);
        if (p.operational_focus) setFocus(p.operational_focus);
        if (p.per_state_data && typeof p.per_state_data === "object") {
          setPerStateData(p.per_state_data);
        }
        // Stage 3 (Prompt B) — hydrate new signals if merchant has already
        // answered them. Null/undefined stays null (Provisional).
        if (p.has_correction !== undefined && p.has_correction !== null) setHasCorrection(p.has_correction);
        if (p.has_portability !== undefined && p.has_portability !== null) setHasPortability(p.has_portability);
        if (p.has_sensitive_data_disclosure) setHasSensitiveDisclosure(p.has_sensitive_data_disclosure);
        if (p.is_sba_small_business !== undefined && p.is_sba_small_business !== null) {
          setIsSbaSmallBusiness(p.is_sba_small_business);
        }
        // Batch 4 — hydrate sector-specific signals if present
        const _hydrate = (val, setter) => {
          if (val !== undefined && val !== null) setter(val);
        };
        _hydrate(p.collects_biometric_data, setCollectsBiometric);
        _hydrate(p.has_biometric_consent, setHasBiometricConsent);
        _hydrate(p.has_biometric_retention_schedule, setHasBiometricRetention);
        _hydrate(p.has_biometric_no_sale, setHasBiometricNoSale);
        _hydrate(p.collects_health_data, setCollectsHealth);
        _hydrate(p.has_mhmda_specific_policy, setHasMhmdaPolicy);
        _hydrate(p.has_health_data_opt_in_consent, setHasHealthOptIn);
        _hydrate(p.has_health_data_sale_authorization, setHasHealthSaleAuth);
        _hydrate(p.has_health_data_withdrawal_right, setHasHealthWithdrawal);
        _hydrate(p.has_health_data_deletion_right, setHasHealthDeletion);
        if (p.per_state_consumer_tier && typeof p.per_state_consumer_tier === "object") {
          setPerStateConsumerTier(p.per_state_consumer_tier);
        }
        // Horizontal A (2026-08-16) — hydrate data-broker flag if present.
        if (p.sells_personal_data !== undefined && p.sells_personal_data !== null) {
          setSellsPersonalData(p.sells_personal_data);
        }
      }
    }).catch(()=>{});
  }, []);

  // Bug (2026-07-24, Option B) — Auto-flip guard for the profile-hydration
  // edge case. `toggleJuris` handles the auto-switch when a merchant adds
  // a limited state via the state picker, but a stale saved profile can
  // land the merchant here already in the invalid combo (score_mode="estimated"
  // + selected contains a limited state). Watch both and correct.
  // Piece 1B (2026-08-16) — auto-flip guard relaxed. Previously fired for
  // ANY comprehensive-state selection. Now the state is only "limited" if
  // the merchant hasn't answered the optional consumer-tier picker yet.
  // Answered tier ⇒ scoring resolves in Estimated mode; no flip needed.
  useEffect(() => {
    if (scoreMode !== "estimated") return;
    const unresolved = selected.some(
      (abbr) => ESTIMATED_MODE_LIMITED_STATES.includes(abbr) && !perStateConsumerTier[abbr]
    );
    if (unresolved) {
      // Do NOT auto-flip anymore — blank stays honestly Provisional per
      // the approved Piece 1B plan. The banner + Switch-to-Verified
      // button surface the option without forcing it.
    }
  }, [scoreMode, selected, perStateConsumerTier]);

  const estimatedLimitedSelected = useMemo(
    () => selected.filter(
      (abbr) => ESTIMATED_MODE_LIMITED_STATES.includes(abbr) && !perStateConsumerTier[abbr]
    ),
    [selected, perStateConsumerTier]
  );
  const estimatedBlocked = false;  // Piece 1B — never hard-block; blank = Provisional
  // Human-readable "IL (Illinois), WA (Washington)"-style list for the banner.
  const estimatedLimitedLabel = useMemo(() => {
    const byAbbr = (a) => {
      const meta = JURIS.find((j) => j.abbr === a);
      return meta ? `${meta.name} (${a})` : a;
    };
    return estimatedLimitedSelected.map(byAbbr).join(", ");
  }, [estimatedLimitedSelected]);

  // Constraint 1 — normalize for hard-block check. Mirrors the backend
  // `normalize_for_classification()` so client + server agree on what's blocked.
  const HARD_BLOCK_DOMAINS = [
    "youtube.com", "facebook.com", "instagram.com", "twitter.com", "x.com",
    "tiktok.com", "reddit.com", "linkedin.com", "wikipedia.org", "google.com",
  ];
  const normalizeForBlock = (raw) => {
    let s = (raw || "").trim().toLowerCase();
    if (!s) return "";
    if (s.startsWith("https://")) s = s.slice(8);
    else if (s.startsWith("http://")) s = s.slice(7);
    if (s.startsWith("www.")) s = s.slice(4);
    const cut = s.search(/[/?#]/);
    if (cut >= 0) s = s.slice(0, cut);
    return s.replace(/\.+$/, "");
  };
  const isHardBlocked = (raw) => {
    const host = normalizeForBlock(raw);
    if (!host) return false;
    return HARD_BLOCK_DOMAINS.some((d) => host.includes(d));
  };

  // Constraint 2 — error must be attached to state, and Next must be
  // disabled while error is active.
  const storeUrlError = (() => {
    const v = (storeUrl || "").trim();
    if (!v) return "";
    if (isHardBlocked(v)) {
      return (
        "This does not appear to be a Shopify store. Regulens is built " +
        "exclusively for Shopify merchants. Please enter your store URL."
      );
    }
    return "";
  })();

  const initVerify = async () => {
    if (!storeUrl.trim()) return toast.error("Enter your Shopify store URL");
    if (!storeConfirmed) return toast.error("Please confirm this is your store");
    // Constraint 2 — hard-stop, never proceed if validation is active.
    if (storeUrlError) return;
    setBusy(true);
    try {
      const { data } = await api.post("/audit/store/self-confirm", { shopify_store_url: storeUrl });
      setStoreVerified(true);
      // Tier-3: graceful degradation — onboarding still proceeds.
      if (data?.store_url_unverified) {
        toast.message("Store added — automated verification pending.", {
          description: "Your score will show an 'Unverified' badge until we confirm.",
          duration: 6000,
        });
      } else {
        toast.success("Store confirmed");
      }
      setStep(1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to confirm store");
    } finally {
      setBusy(false);
    }
  };

  const toggleJuris = (abbr) => {
    // Piece 1B (2026-08-16) — auto-flip on state-add REMOVED. The
    // optional consumer-tier picker now handles applicability for the
    // 8 comprehensive-privacy states directly in Estimated mode.
    // Blank tier = honest Provisional per the approved plan.
    setSelected((s) => (s.includes(abbr) ? s.filter((x) => x !== abbr) : [...s, abbr]));
  };

  // Pure helpers for per-state Verified inputs.
  const setStateField = (abbr, field, value) =>
    setPerStateData((d) => ({
      ...d,
      [abbr]: { ...(d[abbr] || {}), [field]: value },
    }));

  const nyResult = useMemo(() => {
    if (scoreMode !== "verified" || !selected.includes("NY")) return null;
    const ny = perStateData.NY || {};
    const r = parseFloat(ny.revenue) || 0;
    const t = parseInt(ny.transactions) || 0;
    if (!ny.revenue && !ny.transactions) return null;
    const revMet = r >= 500000;
    const txnMet = t >= 100;
    if (revMet && txnMet) return { kind: "triggered", msg: "Both $500K revenue AND 100+ transactions met. NY nexus is likely triggered." };
    if (revMet && !txnMet) return { kind: "partial", msg: `Revenue threshold met. Transaction count (${t}) below 100. NY nexus is NOT triggered under current law.` };
    if (!revMet && txnMet) return { kind: "partial", msg: `Transactions threshold met (${t} ≥ 100). Revenue ($${r.toLocaleString()}) below $500K. NY nexus is NOT triggered.` };
    return { kind: "none", msg: "Neither NY threshold met. NY nexus NOT triggered." };
  }, [scoreMode, selected, perStateData]);

  const scrapePolicies = async () => {
    setBusy(true);
    setFirewallMsg(null);
    try {
      const { data } = await api.post("/audit/policies/scrape");
      if (data.privacy_policy) setPp(data.privacy_policy);
      if (data.terms_of_service) setTos(data.terms_of_service);
      if (data.refund_policy) setRefund(data.refund_policy);
      setScrapeStatus({ privacy: data.privacy_policy_found, terms: data.terms_of_service_found, refund: data.refund_policy_found });
      if (data.firewall_blocked && data.firewall_message) {
        setFirewallMsg(data.firewall_message);
      } else {
        toast.success("Scraping complete");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Scrape failed"); } finally { setBusy(false); }
  };

  const job = useAuditJobPolling(api);

  // Build the audit payload once so retry can reuse it.
  const buildPayload = () => {
    const isVerified = scoreMode === "verified";
    // For Verified mode: clean per_state_data so it only contains selected states with numeric values.
    const psd = {};
    if (isVerified) {
      // Comprehensive-privacy states gate applicability on unique-consumer
      // counts (not transactions). CA/VA/CO score ACTIVE today;
      // IA/IN/KY/MN/TN join via Prompt B Batch 2 (PENDING_REVIEW). Preserve
      // `consumers` when the merchant provided it so the backend applicability
      // check has the right input. NE is excluded — it uses SBA attestation,
      // not a consumer count.
      const COMPREHENSIVE_CONSUMER_STATES = new Set([
        "CA", "VA", "CO", "IA", "IN", "KY", "MN", "TN",
        "AL", "OK", "LA", "VT",
      ]);
      selected.forEach((abbr) => {
        const block = perStateData[abbr] || {};
        const entry = {
          revenue: parseFloat(block.revenue) || 0,
          transactions: parseInt(block.transactions) || 0,
        };
        // 2026-08-18 — Verified-mode blank-consumer guard. Prior code
        // OMITTED the `consumers` key when blank, which the backend then
        // silently coerced to 0 → state marked Not Applicable. That
        // violated "blank = honest Provisional, never guess."
        // Fix: send `consumers: null` EXPLICITLY when blank so the backend's
        // provisional_caveat branch (services/scoring.py) fires. Typed
        // values still parse to float. NE is intentionally excluded — it
        // uses SBA attestation, not a consumer count.
        if (COMPREHENSIVE_CONSUMER_STATES.has(abbr)) {
          if (block.consumers === undefined || block.consumers === "" || block.consumers === null) {
            entry.consumers = null;
          } else {
            entry.consumers = parseFloat(block.consumers) || 0;
          }
        }
        psd[abbr] = entry;
      });
    }
    // Legacy NY fields mirror per_state_data.NY when in Verified mode.
    const ny = isVerified ? (perStateData.NY || {}) : {};
    return {
      score_mode: scoreMode,
      annual_revenue_tier: revenueTier,
      annual_revenue_exact:
        revenueExact !== "" && !isNaN(parseInt(revenueExact))
          ? parseInt(revenueExact)
          : null,
      annual_transaction_tier: isVerified ? null : transactionTier,
      selected_jurisdictions: selected,
      operational_focus: focus,
      per_state_data: psd,
      ny_annual_revenue:
        isVerified && ny.revenue !== undefined && ny.revenue !== ""
          ? parseFloat(ny.revenue)
          : null,
      ny_annual_transactions:
        isVerified && ny.transactions !== undefined && ny.transactions !== ""
          ? parseInt(ny.transactions)
          : null,
      privacy_policy_text: pp,
      terms_of_service_text: tos,
      refund_policy_text: refund,
      cookie_consent_present: cookieC,
      privacy_contact_present: pcontact,
      retention_policy_present: retention,
      jurisdictions_identified: jIdent,
      nexus_documented: nDoc,
      nexus_registered_states: Object.fromEntries(
        selected.map((s) => [s, !!regStates[s]])
      ),
      active_monitoring: aMon,
      // Stage 3 (Prompt B) — pass null (unanswered) through so backend
      // Provisional detection can distinguish "not answered" from "No".
      has_correction: hasCorrection,
      has_portability: hasPortability,
      has_sensitive_data_disclosure: hasSensitiveDisclosure,
      // Prompt B Batch 2 — self-attested SBA small-business flag (NDPA).
      // Passed as null (unanswered) when NE isn't selected or the merchant
      // hasn't answered yet, so the backend's Provisional branch fires.
      is_sba_small_business: isSbaSmallBusiness,
      // Batch 4 — sector-specific privacy law signals (IL BIPA + WA MHMDA)
      collects_biometric_data: collectsBiometric,
      has_biometric_consent: hasBiometricConsent,
      has_biometric_retention_schedule: hasBiometricRetention,
      has_biometric_no_sale: hasBiometricNoSale,
      collects_health_data: collectsHealth,
      has_mhmda_specific_policy: hasMhmdaPolicy,
      has_health_data_opt_in_consent: hasHealthOptIn,
      has_health_data_sale_authorization: hasHealthSaleAuth,
      has_health_data_withdrawal_right: hasHealthWithdrawal,
      has_health_data_deletion_right: hasHealthDeletion,
      // Piece 1B — Option B: per-state consumer-tier picker (Estimated mode).
      per_state_consumer_tier: perStateConsumerTier,
      // Horizontal A — Data broker self-attestation. Feeds the second-tier
      // applicability branch for CA/VA/CO/IA/IN/KY/MN/TN.
      sells_personal_data: sellsPersonalData,
    };
  };

  // Shared up-front validation for both /run and /profile/save.
  const validateProfileShape = () => {
    if (!revenueTier) {
      toast.error("Select a revenue tier");
      return false;
    }
    // Session 2a — $25M+ tier must be backed by an exact revenue ≥ $25M.
    if (revenueTier === "$25M+") {
      const n = parseInt(revenueExact);
      if (isNaN(n) || n < 25_000_000) {
        toast.error("Enter your exact annual revenue (≥ $25,000,000) for the $25M+ tier");
        return false;
      }
    }
    if (!selected.length) {
      toast.error("Select at least one jurisdiction");
      return false;
    }
    if (!focus) {
      toast.error("Select an audit focus");
      return false;
    }
    if (scoreMode === "estimated") {
      // Piece 1 (2026-08-16) — defence-in-depth guard. `estimatedBlocked`
      // should already be handled upstream by auto-flip + hydration effect,
      // but if a user somehow submits while in the invalid combo, block.
      if (estimatedBlocked) {
        toast.error(`Switch to Verified mode — ${estimatedLimitedLabel} requires per-state details.`);
        return false;
      }
      if (!transactionTier) {
        toast.error("Select a transaction count range");
        return false;
      }
    } else {
      // Verified — every selected state must have revenue + transactions filled.
      // 2026-08-18 — Consumer count for the 8 comprehensive-privacy states
      // is INTENTIONALLY NOT required (blank = honest Provisional). We
      // surface a soft toast so the merchant knows which states will be
      // Provisional, and the inline yellow warning under each blank
      // consumer field also flags this in-place. Never hard-block on
      // consumers — see "blank = honest Provisional, never guess."
      const VERIFIED_COMPREHENSIVE = new Set(["CA", "VA", "CO", "IA", "IN", "KY", "MN", "TN"]);
      const blankConsumers = [];
      for (const abbr of selected) {
        const b = perStateData[abbr] || {};
        if (b.revenue === "" || b.revenue === undefined || b.revenue === null) {
          toast.error(`Enter ${abbr} revenue`);
          return false;
        }
        if (b.transactions === "" || b.transactions === undefined || b.transactions === null) {
          toast.error(`Enter ${abbr} transaction count`);
          return false;
        }
        if (
          VERIFIED_COMPREHENSIVE.has(abbr) &&
          (b.consumers === "" || b.consumers === undefined || b.consumers === null)
        ) {
          blankConsumers.push(abbr);
        }
      }
      if (blankConsumers.length > 0) {
        toast(
          `Consumer count blank for ${blankConsumers.join(", ")} — these states will be marked Provisional in the report. Enter numbers above for a definitive answer.`,
          { duration: 6000 }
        );
      }
    }
    return true;
  };

  const submit = async () => {
    if (!validateProfileShape()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/audit/run", buildPayload());
      job.start(data.job_id);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Audit failed";
      toast.error(detail);
      setBusy(false);
    }
  };

  // Session 2b — states with scoring_policy=ACTIVE that get the rights-review
  // step. Keep in sync with backend `state_privacy_law.scoring_policy`.
  // When a new state's policy flips to ACTIVE via the admin one-click button,
  // append it here so its selectors see the review UI.
  const ACTIVE_PRIVACY_STATES = ["CA", "VA", "CO"];
  const needsRightsReview = useMemo(
    () => selected.some((abbr) => ACTIVE_PRIVACY_STATES.includes(abbr)) && !!pp && pp.trim().length >= 50,
    [selected, pp]
  );

  // P5 — Save profile WITHOUT running audit; goes to confirmation screen.
  const saveProfile = async () => {
    if (!validateProfileShape()) return;
    setBusy(true);
    try {
      await api.post("/audit/profile/save", buildPayload());
      // Session 2b — if merchant selected any ACTIVE-scoring state AND
      // pasted a real policy, route them through the rights review before
      // confirmation. Otherwise skip straight to confirmation.
      setStep(needsRightsReview ? 3 : 4);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save profile");
    } finally {
      setBusy(false);
    }
  };

  // Reset busy + react to terminal job states.
  useEffect(() => {
    if (!job.status) return;
    if (job.status === "complete") {
      toast.success(
        job.result?.composite_score != null
          ? `Your readiness report is ready — score ${job.result.composite_score}/100`
          : "Your readiness report is ready."
      );
      (async () => {
        await refresh();
        navigate("/dashboard");
      })();
    } else if (job.status === "failed") {
      setBusy(false);
    }
  }, [job.status]);

  const handleRetry = async () => {
    job.reset();
    setBusy(true);
    try {
      const { data } = await api.post("/audit/run", buildPayload());
      job.start(data.job_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Audit failed");
      setBusy(false);
    }
  };

  const handleCancel = () => {
    job.reset();
    setBusy(false);
  };

  const progress = ((step + (storeVerified ? 1 : 0)) / 5) * 100;

  return (
    <Layout>
      <Toaster theme="dark" position="top-right" offset={80} />
      {(job.status === "pending" || job.status === "processing" || job.status === "failed") && (
        <AuditRunOverlay
          status={job.status}
          errorMessage={job.errorMessage}
          onRetry={handleRetry}
          onCancel={handleCancel}
        />
      )}
      <section className="max-w-3xl mx-auto px-6 py-10" data-testid="onboarding-wizard">
        {/* Progress (Item 8) */}
        <div className="mb-8" data-testid="onboarding-progress">
          <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--rg-sage)] mb-2">
            {(() => {
              const labels = [
                "Business Details",
                "Revenue Information",
                "State Selection",
                "Privacy Rights Review",
                "Generating Your Score",
              ];
              const idx = Math.min(step, 4);
              return `Step ${idx + 1} of 5 — ${labels[idx]}`;
            })()}
          </div>
          <div className="h-1.5 bg-[var(--rg-surface)] border border-[var(--rg-border)] rounded-sm overflow-hidden">
            <motion.div className="h-full bg-[var(--rg-sage)]" initial={{ width: 0 }} animate={{ width: `${Math.max(progress, 15)}%` }} />
          </div>
        </div>

        {step === 0 && (
          <div className="rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] p-6" data-testid="step-store">
            <h2 className="font-heading font-bold text-2xl text-[var(--rg-text-strong)] mb-2">Step 1 · Your Shopify store</h2>
            <p className="text-[var(--rg-text)] text-sm mb-6">
              Enter your Shopify store URL. Any format works — custom domain or <span className="font-mono">*.myshopify.com</span>.
            </p>

            <Label className="text-[var(--rg-text)] text-xs uppercase tracking-[0.18em]">Shopify Store URL</Label>
            <Input
              placeholder="Enter your shop address (e.g., brandname.com or brandname.myshopify.com)"
              value={storeUrl}
              onChange={(e) => setStoreUrl(e.target.value)}
              aria-invalid={!!storeUrlError}
              aria-describedby={storeUrlError ? "store-url-error" : undefined}
              className={
                "mt-1 w-full max-w-xl bg-[var(--rg-surface)] text-[var(--rg-text-strong)] " +
                (storeUrlError ? "border-[var(--rg-red)] focus-visible:ring-[var(--rg-red)]" : "border-[var(--rg-border)]")
              }
              data-testid="onboarding-store-url"
            />
            {storeUrlError && (
              <div
                id="store-url-error"
                role="alert"
                className="mt-2 text-sm text-[var(--rg-red)] font-medium max-w-xl"
                data-testid="onboarding-store-url-error"
              >
                {storeUrlError}
              </div>
            )}

            <label className="flex items-start gap-2 mt-4 cursor-pointer" data-testid="onboarding-store-confirm-row">
              <Checkbox
                checked={storeConfirmed}
                onCheckedChange={(v) => setStoreConfirmed(!!v)}
                className="border-[var(--rg-border-hi)] mt-0.5"
                data-testid="onboarding-store-confirm"
              />
              <span className="text-sm text-[var(--rg-text)]">I confirm this is my Shopify store</span>
            </label>

            <Button
              onClick={initVerify}
              disabled={busy || !storeUrl.trim() || !storeConfirmed || !!storeUrlError}
              className="mt-6 bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-semibold disabled:opacity-40"
              data-testid="onboarding-continue-store"
            >
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Continuing…</> : <>Continue <ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>
          </div>
        )}

        {step === 1 && (
          <div className="rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] p-6 space-y-6" data-testid="step-profile">
            <div>
              <h2 className="font-heading font-bold text-2xl text-[var(--rg-text-strong)] mb-2">Step 2 · Profile & jurisdictions</h2>
              <p className="text-[var(--rg-text)] text-sm">
                Tell us your revenue, transaction volume, and which of the covered jurisdictions you ship or sell to.
              </p>
            </div>

            {/* === Mode toggle === */}
            <div data-testid="onboarding-mode-toggle">
              <Label className="text-[var(--rg-text)] text-xs uppercase tracking-[0.18em]">
                How do you want to enter your numbers?
              </Label>
              <div className="grid sm:grid-cols-2 gap-3 mt-2">
                <button
                  type="button"
                  onClick={() => { if (!estimatedBlocked) setScoreMode("estimated"); }}
                  disabled={estimatedBlocked}
                  aria-disabled={estimatedBlocked}
                  className={`text-left p-4 rounded-md border-2 transition-fast ${
                    estimatedBlocked
                      ? "border-[var(--rg-border)] bg-[var(--rg-surface-2)] opacity-60 cursor-not-allowed"
                      : scoreMode === "estimated"
                      ? "border-[var(--rg-sage)] bg-[var(--rg-sage-soft)]"
                      : "border-[var(--rg-border)] bg-[var(--rg-surface)] hover:border-[var(--rg-border-hi)]"
                  }`}
                  data-testid="onboarding-mode-estimated"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[var(--rg-text-strong)] font-heading font-bold text-sm">Quick Estimate</span>
                    {estimatedBlocked && (
                      <span className="text-[9px] font-mono uppercase tracking-wider text-[var(--rg-amber)] border border-[var(--rg-amber)] px-1.5 py-0.5 rounded-sm" data-testid="onboarding-mode-estimated-unavailable">
                        Not available
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[var(--rg-text)] leading-relaxed">
                    {estimatedBlocked
                      ? <>Some of your selected states ({estimatedLimitedLabel}) need per-state numeric input we only collect in <strong>Detailed Input</strong> mode.</>
                      : <>A fast starting point — pick revenue + transaction ranges and your top states. Score is labelled <strong className="text-[var(--rg-amber)]">Estimated</strong> and uses worst-case math per state.</>}
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => setScoreMode("verified")}
                  className={`text-left p-4 rounded-md border-2 transition-fast ${
                    scoreMode === "verified"
                      ? "border-[var(--rg-sage)] bg-[var(--rg-sage-soft)]"
                      : "border-[var(--rg-border)] bg-[var(--rg-surface)] hover:border-[var(--rg-border-hi)]"
                  }`}
                  data-testid="onboarding-mode-verified"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[var(--rg-text-strong)] font-heading font-bold text-sm">Detailed Input</span>
                    <span className="text-[9px] font-mono uppercase tracking-wider text-[var(--rg-on-sage)] bg-[var(--rg-sage)] px-1.5 py-0.5 rounded-sm" data-testid="onboarding-mode-verified-preferred">Preferred — most accurate</span>
                  </div>
                  <p className="text-xs text-[var(--rg-text)] leading-relaxed">
                    Enter exact revenue + transaction counts per state. Unlocks the public Verified Score badge.
                  </p>
                </button>
              </div>
            </div>

            {/* Piece 1 (2026-08-16) — Provisional-mode banner. Renders whenever
                the merchant has selected a state whose applicability gate is
                only reachable in Verified mode. Dynamic — only names the
                states the merchant actually picked (never the full 8-state
                list unless they selected all 8). One-click switch to Verified. */}
            {estimatedBlocked && (
              <div className="rounded-md border border-[var(--rg-amber)] bg-[var(--rg-amber-soft)] p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="onboarding-provisional-banner">
                <div className="flex-1 text-sm text-[var(--rg-text)]">
                  <span className="font-semibold text-[var(--rg-text-strong)]" data-testid="onboarding-provisional-states">{estimatedLimitedLabel}</span>{" "}
                  {estimatedLimitedSelected.length === 1 ? "requires" : "require"} our Detailed Input mode for an accurate score. Switch now for pinpoint results.
                </div>
                <Button
                  type="button"
                  onClick={() => setScoreMode("verified")}
                  className="bg-[var(--rg-gold)] text-[var(--rg-on-gold)] hover:bg-[var(--rg-gold-hover)] font-semibold shrink-0"
                  data-testid="onboarding-provisional-switch"
                >
                  Switch to Verified
                </Button>
              </div>
            )}

            {/* === Revenue tier (shared) === */}
            <div>
              <Label className="text-[var(--rg-text)] text-xs uppercase tracking-[0.18em]">Annual Revenue Range</Label>
              <Select value={revenueTier} onValueChange={setRevenueTier}>
                <SelectTrigger className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)] mt-1 w-full max-w-xl" data-testid="onboarding-revenue">
                  <SelectValue placeholder="Select revenue range" />
                </SelectTrigger>
                <SelectContent className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)]">
                  {REVENUE_TIERS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Session 2a — optional exact revenue input. Only surfaces when
                the merchant picks $2M+ or $25M+, because that's where the
                tier ceiling causes CCPA-applicability misrepresentation.
                Blank = keep the tier default (existing behavior). */}
            {(revenueTier === "$2M+" || revenueTier === "$25M+") && (
              <div data-testid="onboarding-revenue-exact-wrap">
                <Label className="text-[var(--rg-text)] text-xs uppercase tracking-[0.18em]">
                  Exact Annual Revenue (USD) <span className="text-[var(--rg-muted)] normal-case tracking-normal">— optional, but required for accurate CCPA determination if you're $25M+</span>
                </Label>
                {/* 2026-08-18 — inline explainer to disambiguate this field
                    from the per-state "Annual Revenue" field in Verified
                    mode below. This one is TOTAL COMPANY REVENUE across
                    all jurisdictions — used for federal-level thresholds
                    like CCPA §1798.140(d)(A) ($25M business revenue).
                    The per-state field represents state-attributable
                    revenue and feeds state-level economic-nexus
                    thresholds. Both are read by the scoring engine
                    (services/scoring.py line 462: `max(ps_revenue,
                    exact_revenue, est_revenue)`). */}
                <p className="text-[11px] text-[var(--rg-sage)] mt-1 leading-snug font-medium" data-testid="onboarding-revenue-exact-hint">
                  Total company revenue across ALL states — used for federal-level triggers like the CCPA $25M threshold.
                </p>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={revenueExact}
                  onChange={(e) => setRevenueExact(e.target.value.replace(/[^0-9]/g, ""))}
                  placeholder={revenueTier === "$25M+" ? "e.g., 25000000" : "e.g., 3500000"}
                  className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)] mt-1 w-full max-w-xl font-mono"
                  data-testid="onboarding-revenue-exact"
                />
                <p className="text-[10px] text-[var(--rg-muted)] mt-1">
                  Your exact revenue overrides the tier estimate for privacy-law applicability.
                  {revenueTier === "$25M+" && " Value must be at least $25,000,000."}
                </p>
              </div>
            )}

            {/* === Transaction tier (Quick Estimate only) === */}
            {scoreMode === "estimated" && (
              <div data-testid="onboarding-transaction-tier-wrap">
                <Label className="text-[var(--rg-text)] text-xs uppercase tracking-[0.18em]">Total Annual Transaction Count</Label>
                <Select value={transactionTier} onValueChange={setTransactionTier}>
                  <SelectTrigger className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)] mt-1 w-full max-w-xl" data-testid="onboarding-transaction-tier">
                    <SelectValue placeholder="Select transaction range" />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)]">
                    {TRANSACTION_TIERS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-[var(--rg-muted)] mt-1">
                  These are your <strong>total business-wide</strong> ranges — the same totals are applied to <strong>every</strong> state you select below (worst-case-per-state evaluation). To enter per-state numbers, switch to <strong>Exact Numbers</strong> above.
                </p>
              </div>
            )}

            {/* === 50-state selector (shared) === */}
            <div>
              <JurisdictionSelector
                selected={selected}
                filter={jurisFilter}
                onFilterChange={setJurisFilter}
                onToggle={toggleJuris}
                onClear={() => setSelected([])}
              />
              {scoreMode === "estimated" && (
                <p className="text-xs text-[var(--rg-text)] mt-2" data-testid="onboarding-top-states-hint">
                  💡 We recommend selecting your <strong>top 3-5 states</strong> by shipping volume. The score evaluates each state independently using your full revenue + transaction totals (worst-case-per-state).
                </p>
              )}
              {selected.length === 0 && (
                <p className="text-xs text-[var(--rg-red)] mt-2" data-testid="onboarding-zero-juris-error">
                  Please select at least one state to continue. Regulens requires at least one jurisdiction to generate a readiness report.
                </p>
              )}
            </div>

            <NexusRegistrationSection
              selected={selected}
              regStates={regStates}
              onChange={(abbr, value) => setRegStates((s) => ({ ...s, [abbr]: value }))}
            />

            {/* ================================================================
                Piece 1 (2026-08-16) — Sector-specific Applicability
                ================================================================
                Renders in BOTH modes (Estimated + Verified) whenever NE/IL/WA
                is selected. These three states have applicability gates that
                are single self-attested booleans (SBA status, biometric
                collection, health-data collection) — NOT per-state numeric
                inputs — so they can score fully in Estimated mode as long as
                the merchant answers.
                (Previously these blocks lived inside the Verified per-state
                loop, which silently produced Provisional-forever outcomes for
                Estimated-mode users.)
            ================================================================ */}
            {(selected.includes("NE") || selected.includes("IL") || selected.includes("WA")) && (
              <div className="rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] p-5 space-y-5" data-testid="sector-applicability-section">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[var(--rg-sage)] mb-1">Applicability Signals</div>
                  <h3 className="font-heading font-bold text-[var(--rg-text-strong)] text-lg">Sector-specific applicability</h3>
                  <p className="text-xs text-[var(--rg-muted)] mt-1">
                    A few quick yes/no questions for the states you selected that gate applicability on business type rather than per-state consumer counts. Available in both Quick Estimate and Detailed Input modes.
                  </p>
                </div>

                {/* NE SBA */}
                {selected.includes("NE") && (
                  <div data-testid="sector-NE-sba-wrap">
                    <Label className="text-[var(--rg-text)] text-[11px] uppercase tracking-[0.18em]">Nebraska NDPA — SBA Small Business Status</Label>
                    <div className="flex gap-2 mt-2">
                      <Button type="button" variant="outline" onClick={() => setIsSbaSmallBusiness(true)}
                        className={`border-[var(--rg-border)] text-xs ${isSbaSmallBusiness === true ? "bg-[var(--rg-border)] text-[var(--rg-text-strong)]" : "text-[var(--rg-text)]"}`}
                        data-testid="sector-NE-sba-yes">Yes — SBA small business</Button>
                      <Button type="button" variant="outline" onClick={() => setIsSbaSmallBusiness(false)}
                        className={`border-[var(--rg-border)] text-xs ${isSbaSmallBusiness === false ? "bg-[var(--rg-border)] text-[var(--rg-text-strong)]" : "text-[var(--rg-text)]"}`}
                        data-testid="sector-NE-sba-no">No — NDPA applies</Button>
                      {isSbaSmallBusiness !== null && (
                        <Button type="button" variant="outline" onClick={() => setIsSbaSmallBusiness(null)}
                          className="border-[var(--rg-border)] text-xs text-[var(--rg-muted)]" data-testid="sector-NE-sba-clear">Clear</Button>
                      )}
                    </div>
                    <p className="text-[10px] text-[var(--rg-muted)] mt-2 leading-snug">
                      Nebraska&apos;s NDPA applies to businesses NOT classified as SBA small businesses. Size standards vary by NAICS code — see{" "}
                      <a href="https://www.sba.gov/size-standards" target="_blank" rel="noreferrer" className="underline text-[var(--rg-text)]">sba.gov/size-standards</a>.
                    </p>
                  </div>
                )}

                {/* IL BIPA */}
                {selected.includes("IL") && (
                  <div className="border-l-2 border-[var(--rg-red)] pl-3" data-testid="sector-IL-bipa-wrap">
                    <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--rg-red)]">⚡ Private Right of Action — Elevated Risk</div>
                    <Label className="text-[var(--rg-text)] text-[11px] uppercase tracking-[0.18em] mt-1 block">Illinois BIPA — Biometric Data Collection</Label>
                    <div className="flex gap-2 mt-2">
                      <Button type="button" variant="outline" onClick={() => setCollectsBiometric(true)}
                        className={`border-[var(--rg-border)] text-xs ${collectsBiometric === true ? "bg-[var(--rg-border)] text-[var(--rg-text-strong)]" : "text-[var(--rg-text)]"}`}
                        data-testid="sector-IL-collects-biometric-yes">Yes — collect biometrics</Button>
                      <Button type="button" variant="outline" onClick={() => setCollectsBiometric(false)}
                        className={`border-[var(--rg-border)] text-xs ${collectsBiometric === false ? "bg-[var(--rg-border)] text-[var(--rg-text-strong)]" : "text-[var(--rg-text)]"}`}
                        data-testid="sector-IL-collects-biometric-no">No</Button>
                      {collectsBiometric !== null && (
                        <Button type="button" variant="outline" onClick={() => setCollectsBiometric(null)}
                          className="border-[var(--rg-border)] text-xs text-[var(--rg-muted)]" data-testid="sector-IL-collects-biometric-clear">Clear</Button>
                      )}
                    </div>
                    <p className="text-[10px] text-[var(--rg-muted)] mt-2 leading-snug">
                      Do you collect fingerprints, facial geometry, retina/iris scans, voiceprints, or hand geometry (e.g., through security cameras, AI fitting tools, or biometric authentication)? BIPA carries statutory damages of $1,000–$5,000 per violation and a private right of action.
                    </p>
                    {collectsBiometric === true && (
                      <div className="mt-3 space-y-2">
                        {[
                          ["Informed written consent obtained BEFORE collection?", hasBiometricConsent, setHasBiometricConsent, "IL-consent"],
                          ["Written retention & destruction schedule published?", hasBiometricRetention, setHasBiometricRetention, "IL-retention"],
                          ["Confirmed you do NOT sell biometric data?", hasBiometricNoSale, setHasBiometricNoSale, "IL-no-sale"],
                        ].map(([label, val, setter, tid]) => (
                          <div key={tid} className="flex items-center justify-between">
                            <span className="text-[11px] text-[var(--rg-text)] leading-snug pr-3">{label}</span>
                            <div className="flex gap-1">
                              <Button type="button" variant="outline" onClick={() => setter(true)}
                                className={`h-6 px-2 text-[10px] border-[var(--rg-border)] ${val === true ? "bg-[var(--rg-border)] text-[var(--rg-text-strong)]" : "text-[var(--rg-text)]"}`}
                                data-testid={`sector-${tid}-yes`}>Yes</Button>
                              <Button type="button" variant="outline" onClick={() => setter(false)}
                                className={`h-6 px-2 text-[10px] border-[var(--rg-border)] ${val === false ? "bg-[var(--rg-border)] text-[var(--rg-text-strong)]" : "text-[var(--rg-text)]"}`}
                                data-testid={`sector-${tid}-no`}>No</Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* WA MHMDA */}
                {selected.includes("WA") && (
                  <div className="border-l-2 border-[var(--rg-red)] pl-3" data-testid="sector-WA-mhmda-wrap">
                    <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--rg-red)]">⚡ Private Right of Action — Elevated Risk</div>
                    <Label className="text-[var(--rg-text)] text-[11px] uppercase tracking-[0.18em] mt-1 block">Washington MHMDA — Consumer Health Data</Label>
                    <div className="flex gap-2 mt-2">
                      <Button type="button" variant="outline" onClick={() => setCollectsHealth(true)}
                        className={`border-[var(--rg-border)] text-xs ${collectsHealth === true ? "bg-[var(--rg-border)] text-[var(--rg-text-strong)]" : "text-[var(--rg-text)]"}`}
                        data-testid="sector-WA-collects-health-yes">Yes — collect health data</Button>
                      <Button type="button" variant="outline" onClick={() => setCollectsHealth(false)}
                        className={`border-[var(--rg-border)] text-xs ${collectsHealth === false ? "bg-[var(--rg-border)] text-[var(--rg-text-strong)]" : "text-[var(--rg-text)]"}`}
                        data-testid="sector-WA-collects-health-no">No</Button>
                      {collectsHealth !== null && (
                        <Button type="button" variant="outline" onClick={() => setCollectsHealth(null)}
                          className="border-[var(--rg-border)] text-xs text-[var(--rg-muted)]" data-testid="sector-WA-collects-health-clear">Clear</Button>
                      )}
                    </div>
                    <p className="text-[10px] text-[var(--rg-muted)] mt-2 leading-snug">
                      Do you collect &quot;consumer health data&quot; as defined by MHMDA? Includes fitness/wellness data, reproductive-health-related purchases, mental-health information, supplements/vitamins, or any product data reflecting a consumer&apos;s health status. Broadly defined by statute.
                    </p>
                    {collectsHealth === true && (
                      <div className="mt-3 space-y-2">
                        {[
                          ["Separate MHMDA-specific health-data privacy policy (distinct from your general one)?", hasMhmdaPolicy, setHasMhmdaPolicy, "WA-policy"],
                          ["Affirmative opt-in consent obtained BEFORE collecting health data?", hasHealthOptIn, setHasHealthOptIn, "WA-opt-in"],
                          ["Written authorization obtained for any SALE of health data (opt-out not sufficient)?", hasHealthSaleAuth, setHasHealthSaleAuth, "WA-sale-auth"],
                          ["Right to withdraw consent operationalized?", hasHealthWithdrawal, setHasHealthWithdrawal, "WA-withdrawal"],
                          ["Right to delete health data honored?", hasHealthDeletion, setHasHealthDeletion, "WA-deletion"],
                        ].map(([label, val, setter, tid]) => (
                          <div key={tid} className="flex items-center justify-between">
                            <span className="text-[11px] text-[var(--rg-text)] leading-snug pr-3">{label}</span>
                            <div className="flex gap-1">
                              <Button type="button" variant="outline" onClick={() => setter(true)}
                                className={`h-6 px-2 text-[10px] border-[var(--rg-border)] ${val === true ? "bg-[var(--rg-border)] text-[var(--rg-text-strong)]" : "text-[var(--rg-text)]"}`}
                                data-testid={`sector-${tid}-yes`}>Yes</Button>
                              <Button type="button" variant="outline" onClick={() => setter(false)}
                                className={`h-6 px-2 text-[10px] border-[var(--rg-border)] ${val === false ? "bg-[var(--rg-border)] text-[var(--rg-text-strong)]" : "text-[var(--rg-text)]"}`}
                                data-testid={`sector-${tid}-no`}>No</Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ================================================================
                Piece 1B (2026-08-16) — Option B: Optional consumer-tier picker
                ================================================================
                Rendered in Estimated mode when a comprehensive-privacy state
                (CA/VA/CO/IA/IN/KY/MN/TN) is selected. Answered tier drives
                backend applicability. Blank stays honestly Provisional —
                no guessed number ever, per approved plan.
            ================================================================ */}
            {scoreMode === "estimated" && selected.some((a) => COMPREHENSIVE_STATES.includes(a)) && (
              <div className="rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] p-5 space-y-4" data-testid="consumer-tier-section">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[var(--rg-sage)] mb-1">Consumer Volume · Optional</div>
                  <h3 className="font-heading font-bold text-[var(--rg-text-strong)] text-lg">Unique consumers per state (optional)</h3>
                  <p className="text-xs text-[var(--rg-muted)] mt-1">
                    Comprehensive privacy laws apply based on unique consumers, not transactions. If you know your rough count for a state, pick a range. If you leave it blank, we'll mark that state as Provisional and offer a clear path to Detailed Input mode — no guessed number.
                  </p>
                </div>
                {selected.filter((a) => COMPREHENSIVE_STATES.includes(a)).map((abbr) => {
                  const meta = JURIS.find((j) => j.abbr === abbr);
                  const current = perStateConsumerTier[abbr];
                  return (
                    <div key={abbr} data-testid={`consumer-tier-${abbr}`}>
                      <Label className="text-[var(--rg-text)] text-[11px] uppercase tracking-[0.18em]">
                        {meta?.name || abbr} ({abbr}) unique consumers
                      </Label>
                      <div className="flex gap-2 mt-2 flex-wrap">
                        {CONSUMER_TIERS.map((t) => (
                          <Button key={t.value} type="button" variant="outline"
                            onClick={() => setPerStateConsumerTier((s) => ({ ...s, [abbr]: t.value }))}
                            className={`border-[var(--rg-border)] text-xs ${current === t.value ? "bg-[var(--rg-border)] text-[var(--rg-text-strong)]" : "text-[var(--rg-text)]"}`}
                            data-testid={`consumer-tier-${abbr}-${t.value}`}
                          >{t.label}</Button>
                        ))}
                        {current && (
                          <Button type="button" variant="outline"
                            onClick={() => setPerStateConsumerTier((s) => { const n = { ...s }; delete n[abbr]; return n; })}
                            className="border-[var(--rg-border)] text-xs text-[var(--rg-muted)]"
                            data-testid={`consumer-tier-${abbr}-clear`}
                          >Clear</Button>
                        )}
                      </div>
                      {abbr === "TN" && current === "100k_plus" && (
                        <p className="text-[10px] text-[var(--rg-amber)] mt-1.5 leading-snug" data-testid="consumer-tier-TN-caveat">
                          TN TIPA applies at 175k+ consumers. Your "100k+" answer may or may not reach that — switch to Detailed Input for a definitive answer.
                        </p>
                      )}
                    </div>
                  );
                })}

                {/* Horizontal A (2026-08-16) — Data broker toggle. Feeds the
                    second-tier applicability branch in scoring.py for
                    CA/VA/CO/IA/IN/KY/MN/TN (≥25k consumers AND sale-of-PI).
                    Before this UI existed the branch defaulted to False for
                    every merchant, so real data brokers with 25k–100k
                    consumers were incorrectly marked Not Applicable. */}
                <div className="pt-3 border-t border-[var(--rg-border)]" data-testid="sells-personal-data-section">
                  <Label className="text-[var(--rg-text)] text-[11px] uppercase tracking-[0.18em]">
                    Do you sell or share personal data with third parties?
                  </Label>
                  <p className="text-[11px] text-[var(--rg-muted)] mt-1 mb-2 leading-snug">
                    "Sell" means: you derive ≥50% of your gross annual revenue (MN: ≥25%) from selling, licensing, or exchanging consumer personal information — including advertising IDs, hashed emails, or behavioral segments shared with ad networks or analytics providers. Skip if unsure — this only applies to comprehensive-privacy states you've selected above.
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    <Button type="button" variant="outline"
                      onClick={() => setSellsPersonalData(true)}
                      className={`border-[var(--rg-border)] text-xs ${sellsPersonalData === true ? "bg-[var(--rg-border)] text-[var(--rg-text-strong)]" : "text-[var(--rg-text)]"}`}
                      data-testid="sells-personal-data-yes"
                    >Yes — we sell / share personal data</Button>
                    <Button type="button" variant="outline"
                      onClick={() => setSellsPersonalData(false)}
                      className={`border-[var(--rg-border)] text-xs ${sellsPersonalData === false ? "bg-[var(--rg-border)] text-[var(--rg-text-strong)]" : "text-[var(--rg-text)]"}`}
                      data-testid="sells-personal-data-no"
                    >No — we do not</Button>
                    {sellsPersonalData !== null && (
                      <Button type="button" variant="outline"
                        onClick={() => setSellsPersonalData(null)}
                        className="border-[var(--rg-border)] text-xs text-[var(--rg-muted)]"
                        data-testid="sells-personal-data-clear"
                      >Clear</Button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* === Verified-mode per-state inputs === */}
            {scoreMode === "verified" && selected.length > 0 && (
              <div data-testid="verified-per-state-grid">
                <Label className="text-[var(--rg-text)] text-xs uppercase tracking-[0.18em]">
                  Exact numbers per selected state (last 12 months)
                </Label>
                <p className="text-xs text-[var(--rg-muted)] mt-1 mb-2">
                  Enter revenue and transaction count for every selected state. NY uses both inputs for its dual threshold; other states use revenue (transactions tracked for context).
                </p>
                {/* 2026-08-18 — inline disambiguation vs the company-wide
                    "Exact Annual Revenue" field at the top of this step.
                    These per-state values are STATE-ATTRIBUTABLE and feed
                    state-level economic-nexus thresholds (e.g., Virginia's
                    $100k trigger). The company-wide field feeds federal-
                    level thresholds like CCPA's $25M business-revenue rule. */}
                <div className="mb-3 rounded-md border-l-3 border-l-[var(--rg-sage)] bg-[var(--rg-sage-soft)] px-3 py-2" data-testid="verified-per-state-explainer">
                  <p className="text-[11px] text-[var(--rg-text)] leading-snug">
                    <strong className="text-[var(--rg-text-strong)]">Enter revenue attributable to each state.</strong> This is different from the total company revenue above — enter what your customers <em>in that state</em> contribute. State-level economic-nexus laws (e.g., Virginia&apos;s $100k trigger) use these numbers. If a state contributes almost all your revenue, put your full company total there; if you don&apos;t know the split, use your best estimate.
                  </p>
                </div>
                <div className="space-y-3">
                  {selected.map((abbr) => {
                    const stateMeta = JURIS.find((j) => j.abbr === abbr);
                    const block = perStateData[abbr] || {};
                    return (
                      <div
                        key={abbr}
                        className="rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-3"
                        data-testid={`verified-state-${abbr}`}
                      >
                        <div className="text-[var(--rg-text-strong)] font-semibold text-sm mb-2">
                          {stateMeta?.name || abbr}
                          <span className="text-[var(--rg-muted)] font-mono text-xs ml-1">({abbr})</span>
                          {abbr === "NY" && (
                            <span className="ml-2 text-[9px] font-mono uppercase text-[var(--rg-sage)] border border-[var(--rg-sage)] px-1.5 py-0.5 rounded-sm">
                              dual threshold
                            </span>
                          )}
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                          <div>
                            <Label className="text-[var(--rg-text)] text-[10px] uppercase tracking-[0.18em]">
                              Annual Revenue
                            </Label>
                            <NumericInput
                              value={block.revenue ?? ""}
                              onChange={(e) => setStateField(abbr, "revenue", e.target.value)}
                              placeholder="e.g., 250000"
                              className="bg-[var(--rg-bg)] border-[var(--rg-border)] text-[var(--rg-text-strong)] mt-1 font-mono"
                              data-testid={`verified-${abbr}-revenue`}
                            />
                          </div>
                          <div>
                            <Label className="text-[var(--rg-text)] text-[10px] uppercase tracking-[0.18em]">
                              Annual Transactions
                            </Label>
                            <NumericInput
                              value={block.transactions ?? ""}
                              onChange={(e) => setStateField(abbr, "transactions", e.target.value)}
                              placeholder="e.g., 87"
                              className="bg-[var(--rg-bg)] border-[var(--rg-border)] text-[var(--rg-text-strong)] mt-1 font-mono"
                              data-testid={`verified-${abbr}-transactions`}
                            />
                          </div>
                        </div>

                        {/* 2026-07-16 — Consumer count input for
                            comprehensive-privacy states. CA/VA/CO score
                            ACTIVE today; IA/IN/KY/MN/TN join via Prompt B
                            Batch 2; AL/OK/LA/VT join via Batch 3 (all
                            PENDING_REVIEW and enforced not-yet-effective
                            for AL/OK/LA/VT). NE is intentionally excluded
                            here — NDPA applies based on SBA small-business
                            status, not numeric thresholds; see the SBA
                            question below. All these statutes gate
                            applicability on unique-consumer count, NOT
                            transaction count. */}
                        {["CA", "VA", "CO", "IA", "IN", "KY", "MN", "TN", "AL", "OK", "LA", "VT"].includes(abbr) && (
                          <div className="mt-3" data-testid={`verified-${abbr}-consumers-wrap`}>
                            <Label className="text-[var(--rg-text)] text-[10px] uppercase tracking-[0.18em]">
                              {stateMeta?.name || abbr} Consumers (unique residents)
                            </Label>
                            <NumericInput
                              value={block.consumers ?? ""}
                              onChange={(e) => setStateField(abbr, "consumers", e.target.value)}
                              placeholder="e.g., 25000"
                              className="bg-[var(--rg-bg)] border-[var(--rg-border)] text-[var(--rg-text-strong)] mt-1 font-mono max-w-[240px]"
                              data-testid={`verified-${abbr}-consumers`}
                            />
                            <p className="text-[10px] text-[var(--rg-muted)] mt-1 leading-snug">
                              {stateMeta?.name || abbr}&apos;s privacy law applies based on how many {stateMeta?.name || abbr} residents&apos; data you process, not just revenue — enter your best estimate.
                            </p>
                            {/* 2026-08-18 — inline blank-consumer warning.
                                Only fires for the 8 comprehensive-privacy
                                states (CA/VA/CO/IA/IN/KY/MN/TN); AL/OK/LA/VT
                                are PENDING_REVIEW so a blank there is
                                already not scored. Blank = honest Provisional —
                                surfaces the same nudge the report will show. */}
                            {["CA", "VA", "CO", "IA", "IN", "KY", "MN", "TN"].includes(abbr) &&
                              (block.consumers === undefined || block.consumers === "" || block.consumers === null) && (
                                <p
                                  className="text-[11px] text-[var(--rg-amber)] mt-1.5 leading-snug"
                                  data-testid={`verified-${abbr}-consumers-blank-warn`}
                                >
                                  Blank — {abbr} will be marked <strong>Provisional</strong> in the report. Enter a number for a definitive applicability answer.
                                </p>
                              )}
                          </div>
                        )}

                        {/* Piece 1 (2026-08-16) — NE SBA, IL BIPA, WA MHMDA
                            applicability blocks were previously rendered
                            inline here (Verified mode only). They now live
                            in a shared "Sector-specific applicability"
                            section outside the Verified wrapper so those
                            three states can score in Estimated mode too.
                            See renderSectorBlock() below. */}
                      </div>
                    );
                  })}
                </div>
                {nyResult && (
                  <div className="mt-3 rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-3 text-sm" data-testid="ny-live-result">
                    <span className="font-semibold" style={{ color: nyResult.kind === "triggered" ? "var(--rg-red)" : nyResult.kind === "partial" ? "var(--rg-amber)" : "var(--rg-sage)" }}>
                      {nyResult.msg}
                    </span>
                  </div>
                )}
              </div>
            )}

            <div>
              <Label className="text-[var(--rg-text)] text-xs uppercase tracking-[0.18em]">Audit Focus</Label>
              <Select value={focus} onValueChange={setFocus}>
                <SelectTrigger className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)] mt-1" data-testid="onboarding-focus">
                  <SelectValue placeholder="Select audit focus" />
                </SelectTrigger>
                <SelectContent className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)]">
                  <SelectItem value="privacy">Privacy Readiness</SelectItem>
                  <SelectItem value="tax">Sales Tax Readiness</SelectItem>
                  <SelectItem value="both">Both</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(0)} className="border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface)]" data-testid="onboarding-prev-1">Back</Button>
              <Button
                onClick={() => setStep(2)}
                disabled={selected.length === 0}
                className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-semibold disabled:opacity-40"
                data-testid="onboarding-next-1"
              >Next: Policies <ArrowRight className="w-4 h-4 ml-2" /></Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] p-6 space-y-6" data-testid="step-policies">
            <div>
              <h2 className="font-heading font-bold text-2xl text-[var(--rg-text-strong)] mb-2">Step 3 · Policy documents</h2>
              <p className="text-[var(--rg-text)] text-sm mb-3">We&apos;ll try to auto-fetch your policies from your store. You can also paste them manually.</p>
              <Button onClick={scrapePolicies} disabled={busy} variant="outline" className="border-[var(--rg-sage)] text-[var(--rg-sage)] hover:bg-[var(--rg-sage-soft)]" data-testid="scrape-policies">
                {busy ? "Scraping..." : "Auto-fetch from my store"}
              </Button>
              {firewallMsg && (
                <div className="mt-3 rounded-md border border-[var(--rg-amber)] bg-[var(--rg-amber-soft)] p-3 text-sm text-[var(--rg-amber)]" data-testid="firewall-message">
                  {firewallMsg}
                </div>
              )}
            </div>

            {[
              { key: "privacy", label: "Privacy Policy", v: pp, set: setPp, status: scrapeStatus.privacy },
              { key: "terms", label: "Terms of Service", v: tos, set: setTos, status: scrapeStatus.terms },
              { key: "refund", label: "Refund Policy", v: refund, set: setRefund, status: scrapeStatus.refund },
            ].map(({ key, label, v, set, status }) => (
              <div key={key}>
                <div className="flex items-center justify-between">
                  <Label className="text-[var(--rg-text)] text-xs uppercase tracking-[0.18em]">{label}</Label>
                  {status === true && <span className="text-[10px] font-mono uppercase text-[var(--rg-sage)]">Found automatically</span>}
                  {status === false && <span className="text-[10px] font-mono uppercase text-[var(--rg-amber)]">Not found — paste below</span>}
                </div>
                <Textarea value={v} onChange={e=>set(e.target.value)} rows={4} className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)] mt-1" data-testid={`policy-${key}`} />
              </div>
            ))}

            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { v: cookieC, set: setCookieC, label: "Cookie consent banner present", id: "cookie" },
                { v: pcontact, set: setPcontact, label: "Privacy contact in policy", id: "pcontact" },
                { v: retention, set: setRetention, label: "Data retention period disclosed", id: "retention" },
                { v: jIdent, set: setJIdent, label: "Jurisdictions identified internally", id: "jident" },
                { v: nDoc, set: setNDoc, label: "Nexus registrations documented", id: "ndoc" },
                { v: aMon, set: setAMon, label: "Active readiness monitoring", id: "amon" },
              ].map(t => (
                <label key={t.id} className="flex items-center gap-2 rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-3 cursor-pointer hover:border-[var(--rg-border-hi)] transition-fast" data-testid={`toggle-${t.id}`}>
                  <Checkbox checked={t.v} onCheckedChange={t.set} className="border-[var(--rg-border-hi)]" />
                  <span className="text-sm text-[var(--rg-text-strong)]">{t.label}</span>
                </label>
              ))}
            </div>

            {/* Stage 3 (Prompt B) — new signals for comprehensive-privacy states (CA/VA/CO).
                All three MUST be answered for the merchant to receive a scored Pillar 2 in an
                ACTIVE state. Unanswered = Provisional · N/A on the dashboard. */}
            <div className="rounded-md border border-[var(--rg-sage)]/40 bg-[var(--rg-sage)]/5 p-4 space-y-4" data-testid="onboarding-compliance-profile">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[var(--rg-sage)]">Compliance Profile</div>
                <p className="text-sm text-[var(--rg-text)] mt-1 leading-snug">
                  Required for merchants selecting California, Virginia, or Colorado. Leave blank if you don't yet have the process in place — an honest No scores more accurately than skipping.
                </p>
              </div>

              <YesNoRow
                testId="signal-correction"
                label="Do you honor customer requests to correct inaccurate personal data?"
                help="A customer emails saying their name or address on file is wrong. Do you have a documented process to verify and update the record?"
                value={hasCorrection}
                onChange={setHasCorrection}
              />
              <YesNoRow
                testId="signal-portability"
                label="Do you provide customers a downloadable copy of their data in a machine-readable format (e.g., CSV or JSON)?"
                help="This is stronger than just showing data on-screen. Comprehensive laws like CCPA, VCDPA and CPA require the export be portable — usable in other systems, not just human-readable."
                value={hasPortability}
                onChange={setHasPortability}
              />
              <SensitiveDataRow
                testId="signal-sensitive"
                value={hasSensitiveDisclosure}
                onChange={setHasSensitiveDisclosure}
              />
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setStep(1)} className="border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface)]" data-testid="onboarding-prev-2">Back</Button>
                <Button onClick={saveProfile} disabled={busy} className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-bold" data-testid="onboarding-save-profile">
                  {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : <>Save Profile <ArrowRight className="w-4 h-4 ml-2" /></>}
                </Button>
              </div>
              <p className="text-xs text-[var(--rg-muted)] italic mt-1" data-testid="onboarding-save-warning">
                ⚠️ Modifying your store configuration will invalidate your current cached audit and trigger a fresh calculation on your next audit run.
              </p>
            </div>
          </div>
        )}

        {step === 3 && (
          <PrivacyRightsReviewStep
            policyText={pp}
            onBack={() => setStep(2)}
            onComplete={() => setStep(4)}
          />
        )}

        {step === 4 && (
          <div className="rounded-lg border border-[var(--rg-sage)] bg-[var(--rg-surface)] p-8 text-center" data-testid="step-confirmation">
            <div className="rounded-full bg-[var(--rg-sage-soft)] w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <ShieldCheck className="w-8 h-8 text-[var(--rg-sage)]" />
            </div>
            <h2 className="font-heading font-extrabold text-3xl text-[var(--rg-text-strong)] mb-2">You are all set</h2>
            <p className="text-[var(--rg-text)] text-sm mb-6">Your store profile has been saved. You are ready to run your first readiness check.</p>

            <div className="rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-4 mb-6 text-left max-w-md mx-auto space-y-2 text-sm" data-testid="confirmation-summary">
              <div className="flex justify-between"><span className="text-[var(--rg-muted)]">Store:</span><span className="text-[var(--rg-text-strong)] font-mono text-xs truncate ml-2">{storeUrl}</span></div>
              <div className="flex justify-between"><span className="text-[var(--rg-muted)]">Revenue tier:</span><span className="text-[var(--rg-text-strong)]">{revenueTier}</span></div>
              <div className="flex justify-between"><span className="text-[var(--rg-muted)]">Jurisdictions selected:</span><span className="text-[var(--rg-text-strong)] font-mono">{selected.length} state{selected.length === 1 ? "" : "s"}</span></div>
            </div>

            <div className="grid sm:grid-cols-3 gap-3 mb-6 max-w-2xl mx-auto text-left">
              <div className="rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-3 flex items-start gap-2">
                <ShieldCheck className="w-5 h-5 text-[var(--rg-sage)] mt-0.5 flex-shrink-0" />
                <div className="text-xs text-[var(--rg-text)]">Every finding cites an official government source</div>
              </div>
              <div className="rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-3 flex items-start gap-2">
                <Clock className="w-5 h-5 text-[var(--rg-sage)] mt-0.5 flex-shrink-0" />
                <div className="text-xs text-[var(--rg-text)]">Takes about 30 seconds</div>
              </div>
              <div className="rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-3 flex items-start gap-2">
                <Eye className="w-5 h-5 text-[var(--rg-sage)] mt-0.5 flex-shrink-0" />
                <div className="text-xs text-[var(--rg-text)]">No obligation — informational only</div>
              </div>
            </div>

            <Button
              onClick={submit}
              disabled={busy}
              className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-bold text-base px-6 py-3 h-auto"
              data-testid="run-audit"
            >
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running…</> : <>Run My First Audit <ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>
          </div>
        )}
      </section>
    </Layout>
  );
}

/**
 * YesNoRow — labeled yes/no radio with help-text and null (unanswered) state.
 * Used for the two boolean Stage-3 (Prompt B) signals: correction, portability.
 */
function YesNoRow({ testId, label, help, value, onChange }) {
  const btn = (v, txt) => (
    <button
      type="button"
      onClick={() => onChange(v)}
      className={`px-3 py-1 rounded-sm text-xs font-semibold uppercase tracking-wider border transition-fast ${
        value === v
          ? "bg-[var(--rg-sage)] text-[var(--rg-on-sage)] border-[var(--rg-sage)]"
          : "text-[var(--rg-text)] border-[var(--rg-border)] hover:border-[var(--rg-sage)]"
      }`}
      data-testid={`${testId}-${txt.toLowerCase()}`}
    >
      {txt}
    </button>
  );
  return (
    <div>
      <div className="text-sm text-[var(--rg-text-strong)] leading-snug">{label}</div>
      {help && <div className="text-xs text-[var(--rg-muted)] leading-snug mt-1">{help}</div>}
      <div className="flex gap-2 mt-2" data-testid={testId}>
        {btn(true, "Yes")}
        {btn(false, "No")}
        {value === null && (
          <span className="text-[10px] uppercase tracking-wider text-[var(--rg-amber)] font-mono self-center ml-1">
            · Unanswered — Provisional
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * SensitiveDataRow — three-option variant for has_sensitive_data_disclosure.
 * "na" (Doesn't apply) means the merchant doesn't collect sensitive data;
 * that right drops out of the scoring denominator in Stage 4.
 */
function SensitiveDataRow({ testId, value, onChange }) {
  const btn = (v, txt) => (
    <button
      type="button"
      onClick={() => onChange(v)}
      className={`px-3 py-1 rounded-sm text-xs font-semibold uppercase tracking-wider border transition-fast ${
        value === v
          ? "bg-[var(--rg-sage)] text-[var(--rg-on-sage)] border-[var(--rg-sage)]"
          : "text-[var(--rg-text)] border-[var(--rg-border)] hover:border-[var(--rg-sage)]"
      }`}
      data-testid={`${testId}-${v}`}
    >
      {txt}
    </button>
  );
  return (
    <div>
      <div className="text-sm text-[var(--rg-text-strong)] leading-snug">
        If you collect sensitive data (health, precise location, biometric, government ID, race/ethnicity,
        financial account, sexual orientation), does your privacy policy disclose those specific categories
        AND honor customer requests to limit their use or consent?
      </div>
      <div className="text-xs text-[var(--rg-muted)] leading-snug mt-1">
        "Doesn't apply" scores neutral (not counted for/against). Selecting Yes when you do collect
        sensitive data means you both disclose it and honor limit/opt-in requests.
      </div>
      <div className="flex flex-wrap gap-2 mt-2" data-testid={testId}>
        {btn("yes", "Yes")}
        {btn("no", "No")}
        {btn("na", "Doesn't apply — I don't collect sensitive data")}
        {value === null && (
          <span className="text-[10px] uppercase tracking-wider text-[var(--rg-amber)] font-mono self-center ml-1">
            · Unanswered — Provisional
          </span>
        )}
      </div>
    </div>
  );
}


